import React, { useMemo } from 'react';
import { formatMoney, formatNumber } from '../../Shared/Formatters';

const SEV = {
    critical: { dot: '🔴', cls: 'sev-critical' },
    warning:  { dot: '🟡', cls: 'sev-warning' },
    info:     { dot: '🔵', cls: 'sev-info' },
};
const DIR = { spike: '↑', drop: '↓', flat: '→' };

function fmt(value, format) {
    if (value == null || isNaN(value)) return '—';
    return format === 'integer' ? formatNumber(value, 0) : formatMoney(value);
}

/**
 * Anomaly list — severity-coloured rows with a z-score bar. Shape from
 * blockBuilder: { items:[{tenant,severity,direction,metric,value,valueFormat,
 * date,z,baseline,baselineFormat,anomalyCount}], labels, totals }.
 * Clicking a row asks for that tenant's sales trend.
 */
export default function AnomalyList({ title, items, labels, totals, onSuggest }) {
    const maxZ = useMemo(
        () => Math.max(3, ...(items || []).map(a => Math.abs(a.z || 0))),
        [items],
    );
    if (!items?.length) return null;

    const footer = [];
    if (totals?.critical) footer.push(`${totals.critical} ${labels?.critical || 'critical'}`);
    if (totals?.warning) footer.push(`${totals.warning} ${labels?.warning || 'warning'}`);

    return (
        <div className="chat-block">
            {title && <div className="chat-block-title">{title}</div>}
            <div className="anomaly-list">
                {items.map((a, i) => {
                    const sev = SEV[a.severity] || SEV.info;
                    const zPct = a.z != null ? Math.min(100, (Math.abs(a.z) / maxZ) * 100) : 0;
                    const clickable = Boolean(onSuggest && a.tenant);
                    return (
                        <div
                            key={i}
                            className={`anomaly-row ${sev.cls}${clickable ? ' clickable' : ''}`}
                            onClick={clickable ? () => onSuggest(`Show ${a.tenant}'s sales trend`) : undefined}
                            title={clickable ? `Show ${a.tenant}'s sales trend` : undefined}
                        >
                            <div className="anomaly-main">
                                <span className="anomaly-sev">{sev.dot}</span>
                                <span className="anomaly-tenant">{a.tenant}</span>
                                <span className="anomaly-metric">
                                    {a.metric} {DIR[a.direction] || ''} {fmt(a.value, a.valueFormat)}
                                </span>
                            </div>
                            <div className="anomaly-sub">
                                <div className="anomaly-zbar">
                                    <div className="anomaly-zbar-fill" style={{ width: `${zPct}%` }} />
                                </div>
                                <span className="anomaly-meta">
                                    {a.date}
                                    {a.z != null && ` · z=${a.z.toFixed(1)}`}
                                    {a.baseline != null && ` · ${labels?.baseline || 'baseline'} ${fmt(a.baseline, a.baselineFormat)}`}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
            {footer.length > 0 && <div className="anomaly-footer">— {footer.join(' · ')}</div>}
        </div>
    );
}
